import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useZonesQuery, useDeleteZoneMutation, Zone } from "@/api/v0/zones";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Edit, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function ZonesRoute() {
  const navigate = useNavigate();
  const [zoneToDelete, setZoneToDelete] = useState<string | null>(null);
  
  const { data: zones, isLoading, isError, refetch } = useZonesQuery();
  const deleteMutation = useDeleteZoneMutation();

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast.success("Zone deleted successfully");
      refetch();
    } catch {
      toast.error("Failed to delete zone");
    } finally {
      setZoneToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-destructive mb-4">Failed to load zones</p>
        <Button onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Zones</h1>
        <Button onClick={() => navigate({ to: "/zones/create" as any })}>
          <Plus className="h-4 w-4 mr-2" />
          Create Zone
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Zone Management</CardTitle>
          <CardDescription>
            Manage your restricted, warning, and information zones.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {zones && zones.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Color</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {zones.map((zone: Zone) => (
                  <TableRow key={zone.id}>
                    <TableCell className="font-medium">{zone.name}</TableCell>
                    <TableCell>
                      <span className="capitalize">{zone.type}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: zone.color }}
                        />
                        {zone.color}
                      </div>
                    </TableCell>
                    <TableCell>
                      {new Date(zone.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() =>
                            navigate({ to: `/zones/${zone.id}/edit` as any })
                          }
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog
                          open={zoneToDelete === zone.id}
                          onOpenChange={(open) =>
                            setZoneToDelete(open ? zone.id : null)
                          }
                        >
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="icon">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Delete Zone
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete the zone "{zone.name}"?
                                This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(zone.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-6">
              <p className="text-muted-foreground mb-4">
                No zones have been created yet.
              </p>
              <Button
                onClick={() => navigate({ to: "/zones/create" as any })}
                variant="outline"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create your first zone
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 